import { supabaseCoreClient } from '../supabase.ts';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { queryClient } from '../constants.js';
import { commonQueryProps } from './cache.js';
import { queryPolicies } from '../queryPolicies.js';
import type { GlobalSettings } from '../../types/metadata.js';
import type { CachedQueryResult } from '../../types/supabase.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

/**
 * Builds the Supabase query for fetching global settings
 * @returns PostgrestBuilder query for global_setting with all necessary columns
 */
export const buildQueryForGlobalSettings = () => {
  return (supabaseCoreClient as any)
    .from('global_setting')
    .select('id,max_rows_for_client_side_table,time_zone,currency,language,date_format,number_format,gmail_keep_alive_duration_days,gmail_keep_alive_table_field_map,gmail_always_sync_all,submission_admin_cc_emails,auto_main_rep_rule,deal_prep_admin_sender_emails,created_at,updated_at,created_by,updated_by')
    .single(); // Since there's always exactly one row
};

// Result type for global settings operations
interface GlobalSettingsResult {
  data: GlobalSettings | null;
  error: any;
}

/**
 * Fetches global settings data outside of React context
 * @returns Promise with global settings data or error
 */
export const getGlobalSettings = async (): Promise<GlobalSettingsResult> => {
  const query = buildQueryForGlobalSettings();
  let settingsData: GlobalSettings | null = null;
  let settingsError: any = null;
  
  try {
    // TypeScript cannot infer the Supabase cache structure - see SupabaseCacheData type definition
    const cachedResult = queryClient.getQueryData(encode(query, false)) as CachedQueryResult<GlobalSettings>;
    settingsData = cachedResult?.data ?? null;
  } catch {
    // Cache miss or error, will fetch from API
  }
  
  if (!settingsData) {
    const { data, error } = await query;
    settingsData = data as GlobalSettings | null;
    settingsError = error;
  }

  if (settingsError) {
    return { data: null, error: settingsError };
  }

  return { data: settingsData, error: null };
};

// Hook options interface
interface UseGlobalSettingsOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseGlobalSettingsResult {
  data: GlobalSettings | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch global settings data using React Query
 * @param options - Options including enabled flag
 * @returns Global settings data with loading and error states
 */
export const useGlobalSettings = ({ enabled = true }: UseGlobalSettingsOptions): UseGlobalSettingsResult => {
  const bootReady = useMetadataBootReady();
  const query = buildQueryForGlobalSettings();

  const { data, error, isPending } = useQuery(
    query,
    {
      ...commonQueryProps,
      staleTime: queryPolicies.configuration.staleTime,
      gcTime: queryPolicies.configuration.gcTime,
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.global_setting'),
    }
  );

  if (isPending || error) {
    return {
      data: null,
      error,
      isLoading: isPending
    };
  }

  return {
    data: data as GlobalSettings | null,
    error: null,
    isLoading: false
  };
};
